import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Alert, Box, Center, Container, Heading, SimpleGrid, Spinner, Text } from '@chakra-ui/react';
import { useGeoUnitFullProfile } from '../hooks/useElectionSim'
import { useUsabilityMark } from '../tools/usability/usabilityMark'
import { classifyQuadrant } from '../components/analyticsMap/bivariate'
import { ProfileHeader } from '../components/districtProfile/ProfileHeader'
import { PoliticalBaselineCard } from '../components/districtProfile/PoliticalBaselineCard'
import { ElectorateCard } from '../components/districtProfile/ElectorateCard'
import { UnionFootprintCard } from '../components/districtProfile/UnionFootprintCard'
import { WhatIfSnapshotCard } from '../components/districtProfile/WhatIfSnapshotCard'
import { PeopleToReachCard } from '../components/districtProfile/PeopleToReachCard'
import { DistrictActionRow } from '../components/districtProfile/DistrictActionRow'
import '../components/districtProfile/print.css'

export function DistrictProfilePage() {
  const { geoUnitId } = useParams<{ geoUnitId: string }>()
  const { data, isLoading, error } = useGeoUnitFullProfile(geoUnitId ?? null)

  const usabilityMark = useUsabilityMark()
  useEffect(() => {
    if (!data) return
    usabilityMark('profile', {
      geoUnitId: data.profile.id,
      name: data.profile.name,
      state: data.profile.state,
    })
  }, [data, usabilityMark])

  if (isLoading) {
    return (
      <Center py={20}>
        <Spinner size="xl" color="accent" />
      </Center>
    )
  }

  if (error || !data) {
    return (
      <Container maxW="container.xl" py={8}>
        <Alert.Root status="error" borderRadius="md">
          <Alert.Indicator />
          District profile not found or an error occurred.
        </Alert.Root>
      </Container>
    );
  }

  const { profile, results, acs, flagRollups, union } = data

  // Quadrant = lean × latest general-election turnout (bivariate classification).
  // Pass the whole cell (or null when an input is missing) so the header can tell
  // "centrist / no-priority-action" (a real result) apart from "data missing".
  const latestGeneralTurnout = [...profile.turnout]
    .filter((t) => !t.suppressed && t.value != null)
    .sort((a, b) => b.year - a.year)[0]
  const quadrantCell =
    profile.lean != null && latestGeneralTurnout
      ? classifyQuadrant(profile.lean, latestGeneralTurnout.value!)
      : null

  return (
    <Container
      className="district-profile-root"
      maxW="container.xl"
      py={8}
      px={{ base: 4, md: 8 }}
    >
      {/* Print-only header: identifies the printout when the app chrome is hidden. */}
      <Box css={{
        display: 'none',
        '& @media print': { display: 'block' }
      }} mb={4}>
        <Heading size="md">{profile.name}</Heading>
        <Text fontSize="sm" color="gray.600">
          {profile.state} · Printed {new Date().toLocaleDateString()}
        </Text>
      </Box>

      <ProfileHeader profile={profile} quadrantCell={quadrantCell} />

      <SimpleGrid columns={{ base: 1, lg: 2 }} gap={4}>
        <PoliticalBaselineCard results={results} turnout={profile.turnout} dropoff={profile.dropoff} />
        <ElectorateCard acs={acs} />
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, lg: 2 }} gap={4}>
        <UnionFootprintCard union={union} geoUnitId={profile.id} unitName={profile.name} />
        <WhatIfSnapshotCard profile={profile} />
      </SimpleGrid>

      <PeopleToReachCard
        flagRollups={flagRollups}
        boundaryWkt={profile.boundaryWkt}
        geoUnitId={profile.id}
        state={profile.state}
        unitName={profile.name}
        unitType={profile.unitType}
      />

      <DistrictActionRow boundaryWkt={profile.boundaryWkt} unitName={profile.name} />
    </Container>
  );
}
